import {
  Component,
  effect,
  inject,
  OnDestroy,
  signal,
  Signal,
  WritableSignal,
} from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormControlStatus,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { AutoCompleteComponent } from '../form-inputs/auto-complete/auto-complete.component';
import { ButtonComponent } from '../../feedback/button/button.component';
import { NgStyle } from '@angular/common';
import { CorporateDesignService } from '../../../services/corporate-design/corporate-design.service';
import { WebTextStore } from '../../../services/api/text/web-text-store.service';
import {
  CONFIGURATION_SETTING_KEYS,
  EEntityType,
  ERelationSearchAccuracy,
  ERelationType,
  INormalText,
  ITariffRelationLocation,
  ITariffRelationVia,
} from '@web/dtos';
import { FilterTextsPipe } from '../../../pipes/filterTexts/filterTexts.pipe';
import { LanguagePipe } from '../../../pipes/language/language.pipe';
import { OriginsStore } from '../../../services/api/relations/origins/origins-store.service';
import { Subscription } from 'rxjs';
import { DestinationsStore } from '../../../services/api/relations/destinations/destinations-store.service';
import { ViasStore } from '../../../services/api/relations/vias/vias-store.service';
import { DropDownComponent } from '../form-inputs/drop-down/drop-down.component';
import { ConfigurationStore } from '../../../services/api/configuration/configuration-store.service';
import { ActiveDirectTicketStore } from '../../../services/api/direct-ticket/active/active-direct-ticket-store.service';
import { RelationDateInputComponent } from '../form-inputs/date-input/relation-date-input/relation-date-input.component';

interface IRelationSearchForm {
  origin: FormControl<ITariffRelationLocation | string | null>;
  destination: FormControl<ITariffRelationLocation | string | null>;
  via: FormControl<ITariffRelationVia | string | null>;
  startDate: FormControl<string | null>;
}

@Component({
  selector: 'aw-relation-search',
  imports: [
    FormsModule,
    ReactiveFormsModule,
    AutoCompleteComponent,
    ButtonComponent,
    NgStyle,
    FilterTextsPipe,
    LanguagePipe,
    DropDownComponent,
    RelationDateInputComponent,
  ],
  templateUrl: './relation-search.component.html',
})
export class RelationSearchComponent implements OnDestroy {
  private readonly formBuilder: FormBuilder = inject(FormBuilder);
  protected readonly directTicketStore: ActiveDirectTicketStore = inject(
    ActiveDirectTicketStore
  );
  protected readonly originsStore: OriginsStore = inject(OriginsStore);
  protected readonly destinationsStore: DestinationsStore =
    inject(DestinationsStore);
  protected readonly viasStore: ViasStore = inject(ViasStore);
  protected readonly corporateDesign: CorporateDesignService = inject(
    CorporateDesignService
  );
  protected readonly configuration: ConfigurationStore =
    inject(ConfigurationStore);
  protected readonly textStore: WebTextStore = inject(WebTextStore);

  protected readonly texts: Signal<INormalText[]> = this.textStore.texts;
  protected readonly origins: Signal<ITariffRelationLocation[]> =
    this.originsStore.origins;
  protected readonly destinations: Signal<ITariffRelationLocation[]> =
    this.destinationsStore.destinations;
  protected readonly vias: Signal<ITariffRelationVia[]> = this.viasStore.vias;

  protected readonly today: Date = new Date();

  protected readonly swap: WritableSignal<
    | { destination: ITariffRelationLocation; origin: ITariffRelationLocation }
    | undefined
  > = signal(undefined);

  protected readonly relationSearchForm: FormGroup<IRelationSearchForm> =
    this.formBuilder.group({
      origin: this.formBuilder.control<ITariffRelationLocation | string>('', [
        Validators.required,
      ]),
      destination: this.formBuilder.control<ITariffRelationLocation | string>(
        '',
        [Validators.required]
      ),
      via: this.formBuilder.control<ITariffRelationVia | string>('', []),
      startDate: this.formBuilder.control<string>('', [Validators.required]),
    });

  protected readonly ERelationType: typeof ERelationType = ERelationType;
  protected readonly EEntityType: typeof EEntityType = EEntityType;
  protected readonly ERelationSearchAccuracy: typeof ERelationSearchAccuracy =
    ERelationSearchAccuracy;

  private readonly subscription: Subscription = new Subscription();

  constructor() {
    effect(() => {
      const swap = this.swap();
      if (
        swap &&
        !this.originsStore.isLoading() &&
        !this.destinationsStore.isLoading()
      ) {
        this.relationSearchForm.controls.origin.setValue(swap.origin);
        this.relationSearchForm.controls.destination.setValue(swap.destination);
      }
    });
    effect(() => {
      if (this.vias().length === 1) {
        this.relationSearchForm.controls.via.setValue(this.vias()[0]);
      }
    });
    if (
      this.configuration.getBooleanConfiguration(
        CONFIGURATION_SETTING_KEYS.IsRelationSearchLoadAllStopsActive
      )
    ) {
      this.subscription.add(
        this.relationSearchForm.controls.origin.statusChanges.subscribe(
          (status: FormControlStatus) => {
            if (status === 'VALID') {
              this.destinationsStore.loadDestinations(
                (
                  this.relationSearchForm.controls.origin
                    .value as ITariffRelationLocation
                )?.externalReference ?? '',
                ''
              );
            }
          }
        )
      );
    }
    this.relationSearchForm.controls.destination.disable();
    this.relationSearchForm.controls.via.disable();
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  protected switchOriginAndDestination(): void {
    const originalOrigin: ITariffRelationLocation = this.relationSearchForm
      .controls.origin.value as ITariffRelationLocation;
    const originalDestination: ITariffRelationLocation = this.relationSearchForm
      .controls.destination.value as ITariffRelationLocation;
    this.originsStore.loadOrigins(
      (originalDestination as ITariffRelationLocation)?.name ?? ''
    );
    this.destinationsStore.loadDestinations(
      (originalOrigin as ITariffRelationLocation)?.name ?? '',
      (originalOrigin as ITariffRelationLocation)?.name ?? ''
    );
    this.swap.set({ destination: originalOrigin, origin: originalDestination });
  }

  protected searchForTickets(): void {
    const search = this.relationSearchForm.value;
    this.directTicketStore.loadRelationTicket({
      origin: (search.origin as ITariffRelationLocation).externalReference,
      destination: (search.destination as ITariffRelationLocation)
        .externalReference,
      dateTime: search.startDate!,
      via: (search.via as ITariffRelationVia)?.via,
    });
  }

  protected loadOrigins(originSearch: string): void {
    if (this.swap()) {
      this.swap.set(undefined);
    }
    if (
      !this.configuration.getBooleanConfiguration(
        CONFIGURATION_SETTING_KEYS.IsRelationSearchLoadAllStopsActive
      )
    ) {
      this.originsStore.loadOrigins(originSearch);
    }
    this.relationSearchForm.controls.destination.reset();
    if (this.relationSearchForm.controls.origin.valid && originSearch) {
      this.relationSearchForm.controls.destination.enable();
    } else {
      this.relationSearchForm.controls.destination.disable();
    }
  }

  protected loadDestinations(destinationSearch: string): void {
    if (
      !this.configuration.getBooleanConfiguration(
        CONFIGURATION_SETTING_KEYS.IsRelationSearchLoadAllStopsActive
      )
    ) {
      this.destinationsStore.loadDestinations(
        (
          this.relationSearchForm.controls.origin
            .value as ITariffRelationLocation
        )?.externalReference ?? '',
        destinationSearch
      );
    }

    if (this.relationSearchForm.controls.destination.valid) {
      this.relationSearchForm.controls.via.enable();
      this.loadVias('');
    } else {
      this.relationSearchForm.controls.via.disable();
      this.relationSearchForm.controls.via.reset();
    }
  }

  protected loadVias(viaSearch: string): void {
    this.viasStore.loadVias(
      (this.relationSearchForm.controls.origin.value as ITariffRelationLocation)
        ?.externalReference ?? '',
      (
        this.relationSearchForm.controls.destination
          .value as ITariffRelationLocation
      )?.externalReference ?? '',
      viaSearch
    );
  }

  protected readonly CONFIGURATION_SETTING_KEYS = CONFIGURATION_SETTING_KEYS;
}
